import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import SecurePasswordInput from "@/components/auth/SecurePasswordInput";
import PasswordMatchIndicator from "@/components/auth/PasswordMatchIndicator";
import { AddressAutocomplete } from "@/components/properties/AddressAutocomplete";
import { useEmailValidation } from "@/hooks/useEmailValidation";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface EmailValidationState {
  isValid: boolean;
  status: 'idle' | 'checking' | 'valid' | 'invalid' | 'wrong-role';
  hasBeenValidated: boolean;
}

interface PersonalInfoFieldsProps {
  formData: {
    fullName: string;
    businessName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    phoneNumber: string;
    email: string;
    password: string;
    confirmPassword: string;
    smsOptIn?: boolean;
  };
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEmailValidationChange?: (validation: EmailValidationState) => void;
}

const PersonalInfoFields = ({ formData, handleChange, onEmailValidationChange }: PersonalInfoFieldsProps) => {
  const { validationState, validateEmail, getValidationMessageData } = useEmailValidation('seller');
  const messageData = getValidationMessageData();

  const handleAddressSelect = (addressData: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  }) => {
    ['address', 'city', 'state', 'zipCode'].forEach(field => {
      const syntheticEvent = {
        target: {
          name: field,
          value: addressData[field as keyof typeof addressData]
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      handleChange(syntheticEvent);
    });
  };

  const handleAddressInputChange = (value: string) => {
    const syntheticEvent = {
      target: {
        name: 'address',
        value: value
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    handleChange(syntheticEvent);
  };

  // Save pending registration on email blur
  const handleEmailBlur = async () => {
    await validateEmail(formData.email);
    
    // Notify parent of validation state change
    onEmailValidationChange?.({
      isValid: validationState.status === 'valid',
      status: validationState.status,
      hasBeenValidated: true
    });
    
    // Fire-and-forget: Save to pending_registrations
    if (formData.email && formData.email.includes('@')) {
      supabase.functions.invoke('save-pending-registration', {
        body: { 
          email: formData.email, 
          user_type: 'seller',
          full_name: formData.fullName || undefined,
          phone_number: formData.phoneNumber || undefined
        }
      }).catch(() => {}); // Silent failure
    }
  };

  // Update parent when validation state changes
  const notifyValidationChange = () => {
    if (validationState.status !== 'idle' && validationState.status !== 'checking') {
      onEmailValidationChange?.({
        isValid: validationState.status === 'valid',
        status: validationState.status,
        hasBeenValidated: true
      });
    }
  };

  // Notify parent on validation state changes
  if (validationState.status !== 'idle' && validationState.status !== 'checking') {
    // Use a timeout to avoid calling during render
    setTimeout(() => notifyValidationChange(), 0);
  }

  return (
    <>
      {/* Email field moved to top for early capture */}
      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <div className="relative">
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="name@example.com"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleEmailBlur}
            required
            className="bg-muted pr-10"
            aria-describedby={validationState.status === 'invalid' ? 'email-error' : undefined}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {validationState.status === 'checking' && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {validationState.status === 'valid' && (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            )}
            {(validationState.status === 'invalid' || validationState.status === 'wrong-role') && (
              <XCircle className="h-4 w-4 text-destructive" />
            )}
          </div>
        </div>
        {messageData?.type === 'wrong-role' && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 mt-2">
            <p className="text-sm text-yellow-700 dark:text-yellow-500 font-medium">
              This email is registered as a {messageData.roleName} account
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              To access {messageData.currentRoleName} features, please use a different email address. Each email can only be associated with one role.
            </p>
            <Link to={messageData.loginPath} className="text-xs text-primary hover:underline mt-1 inline-block">
              Already a {messageData.roleName}? Login here →
            </Link>
          </div>
        )}
        {messageData?.type === 'invalid' && (
          <p id="email-error" className="text-sm text-destructive" role="alert">
            {messageData.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="fullName">Full Name *</Label>
        <Input
          id="fullName"
          name="fullName"
          placeholder="John Doe"
          value={formData.fullName}
          onChange={handleChange}
          required
          className="bg-muted"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="businessName">Business Name (Optional)</Label>
        <Input
          id="businessName"
          name="businessName"
          placeholder="Your Company LLC"
          value={formData.businessName}
          onChange={handleChange}
          className="bg-muted"
        />
      </div>
      
      <AddressAutocomplete
        onAddressSelect={handleAddressSelect}
        onInputChange={handleAddressInputChange}
        initialAddress={formData.address}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="city">City *</Label>
          <Input
            id="city"
            name="city"
            placeholder="City"
            value={formData.city}
            onChange={handleChange}
            required
            className="bg-muted"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="state">State *</Label>
          <Input
            id="state"
            name="state"
            placeholder="State"
            value={formData.state}
            onChange={handleChange}
            required
            className="bg-muted"
            maxLength={2}
          />
        </div>
        
        <div className="space-y-2 md:col-span-3">
          <Label htmlFor="zipCode">ZIP Code *</Label>
          <Input
            id="zipCode"
            name="zipCode"
            placeholder="ZIP Code"
            value={formData.zipCode}
            onChange={handleChange}
            required
            className="bg-muted"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="phoneNumber">Phone Number *</Label>
        <PhoneInput
          id="phoneNumber"
          placeholder="Enter phone number"
          value={formData.phoneNumber}
          onChange={(value) => {
            const syntheticEvent = {
              target: {
                name: 'phoneNumber',
                value: value
              }
            } as React.ChangeEvent<HTMLInputElement>;
            handleChange(syntheticEvent);
          }}
          required
          className="bg-muted"
          showValidation={true}
        />
      </div>

      {/* SMS Opt-In Checkbox - TCPA Compliance */}
      <div className="bg-muted/50 p-3 rounded-md border border-muted">
        <div className="flex items-start space-x-2">
          <Checkbox
            id="smsOptIn"
            checked={formData.smsOptIn || false}
            onCheckedChange={(checked) => {
              const syntheticEvent = {
                target: {
                  name: 'smsOptIn',
                  value: checked
                }
              } as any;
              handleChange(syntheticEvent);
            }}
          />
          <div className="grid gap-1.5 leading-none">
            <Label
              htmlFor="smsOptIn"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              I agree to receive text messages about my properties and account (Optional)
            </Label>
            <p className="text-xs text-muted-foreground">
              By checking this box, you consent to receive informational and transactional SMS notifications about one-time passcodes, property activity, account updates, and platform features from Go Dyspo. Message and data rates may apply. Message frequency may vary. Reply STOP to opt out at any time. View our Privacy Policy at{" "}
              <a 
                href="https://godyspo.com/privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                https://godyspo.com/privacy-policy
              </a>.
            </p>
          </div>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password">Password *</Label>
        <SecurePasswordInput
          id="password"
          value={formData.password}
          onChange={handleChange}
          required
          className="bg-muted"
          showStrengthIndicator={true}
          personalInfo={{
            name: formData.fullName,
            email: formData.email
          }}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm Password *</Label>
        <SecurePasswordInput
          id="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          className="bg-muted"
          autoComplete="new-password"
        />
        <PasswordMatchIndicator 
          password={formData.password} 
          confirmPassword={formData.confirmPassword} 
        />
      </div>
    </>
  );
};

export default PersonalInfoFields;
